// Shared render path for the live-metrics charts (runner & job).
//
// Both hooks were ~95% identical: same axis, same gradient stops, same
// path lazy-create, same line generator. The only honest diff is the
// id namespace + which event names they subscribe to. Factor the render
// pipeline into one function over a typed config; the two hook files
// reduce to thin wrappers that name themselves and hand a samples ring
// to this module.

import {
  computeChart,
  computeChartWindow,
  formatMetricsAxisPct,
  indexedCpu,
  MAX_SAMPLES,
  parseRgbTriple,
  pointX,
  valueColor,
  type ChartPoint,
  type MetricsSample,
  type Rgb,
} from "./job-metrics-chart";
import { computeAxisScales } from "./metrics-axes";
import { cssVar } from "./theme";

/** Pure: (samples, cores, ramBytes) → ChartPoint[] */
export const computeMetricsChart = (
  samples: ReadonlyArray<MetricsSample>,
  cores: number,
  ramBytes: number,
): ChartPoint[] => computeChart(samples, cores, ramBytes);

/**
 * The mutable side of the chart — d3 selections plus a samples ring and
 * the runner denominators. Both metrics hooks already store exactly
 * these fields; this is the shared interface they implement.
 */
export interface MetricsRenderHook {
  _samples: ReadonlyArray<MetricsSample>;
  _chart: ChartPoint[];
  _lastChart?: ChartPoint[] | undefined;
  _lastCpuMax?: number | undefined;
  _lastMemMax?: number | undefined;
  _cores: number;
  _ramBytes: number;
  _ramps?: { cpu: Rgb[]; mem: Rgb[] } | undefined;
  _canvas?: HTMLCanvasElement | undefined;
  _ctx?: CanvasRenderingContext2D | undefined;
  _w: number;
  _h: number;
  _scrollOffsetSlots?: number | undefined;
}

export interface MetricsRenderConfig {
  /** Kept for call-site identity; canvas rendering does not need DOM ids. */
  readonly idBase: string;
  /** Theme-derived gradient ramps (the only theme-y thing this layer sees). */
  readonly rampsFallback: () => { cpu: Rgb[]; mem: Rgb[] };
  /**
   * Optional axis-label formatters. The math always normalises against
   * runner totals (% of cores, % of ramBytes), but a per-job chart on a
   * 104-core / 1 TB shared runner needs to *display* absolute units so
   * a job pegging one core doesn't render as "<1%". Hooks override
   * these to swap the textual axis without disturbing the math.
   * Default: `formatMetricsAxisPct` — % of runner total.
   */
  readonly formatCpuAxis?: (cpuMaxPct: number, cores: number) => string;
  readonly formatMemAxis?: (memMaxPct: number, ramBytes: number) => string;
}

const rgbCss = (rgb: Rgb, alpha = 1): string =>
  `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;

const themeRgb = (name: string, fallback: Rgb): Rgb => {
  const parsed = parseRgbTriple(cssVar(name));
  return parsed[0] === 0 && parsed[1] === 0 && parsed[2] === 0
    ? fallback
    : parsed;
};

const resizeCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): void => {
  const dpr = window.devicePixelRatio || 1;
  const bw = Math.max(1, Math.floor(w * dpr));
  const bh = Math.max(1, Math.floor(h * dpr));
  if (canvas.width !== bw) canvas.width = bw;
  if (canvas.height !== bh) canvas.height = bh;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

const plot = { left: 56, right: 4 };
const edgeClipPx = 3;
const leadingEdgeSamples = 2;

const leadingEdgeChart = (
  samples: ReadonlyArray<MetricsSample>,
  cores: number,
  ramBytes: number,
): ChartPoint[] => {
  const chart = computeChartWindow(
    samples,
    cores,
    ramBytes,
    MAX_SAMPLES + leadingEdgeSamples,
  );
  if (chart.length <= MAX_SAMPLES) return chart;

  const first = chart[0]!;
  const next = chart[1];
  const anchoredChart =
    first.cpuPct.kind === "pending" && next?.cpuPct.kind === "valid"
      ? [{ ...first, cpuPct: next.cpuPct }, ...chart.slice(1)]
      : chart;

  if (anchoredChart.length === MAX_SAMPLES + 1) {
    return [anchoredChart[0]!, ...anchoredChart];
  }

  return anchoredChart;
};

const drawAxisCanvas = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  cpuLabel: string,
  memLabel: string,
): void => {
  const border = themeRgb("--c-border", [80, 80, 80]);
  ctx.save();
  ctx.strokeStyle = rgbCss(border, 0.4);
  ctx.fillStyle = "rgba(255,255,255,1)";
  ctx.font = "9px sans-serif";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.setLineDash([2, 2]);

  const innerH = h - 4 - 4;
  const regionH = Math.max(0, (innerH - 6) / 2);
  const cpuTopY = 4;
  const cpuBaseY = 4 + regionH;
  const memTopY = cpuBaseY + 6;
  const bottomY = h - 4;

  ctx.beginPath();
  ctx.moveTo(56, cpuBaseY + 3);
  ctx.lineTo(w - 4, cpuBaseY + 3);
  ctx.moveTo(56, bottomY);
  ctx.lineTo(w - 4, bottomY);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.fillText(`CPU ${cpuLabel}`, 52, cpuTopY + 3);
  ctx.fillText(`MEM ${memLabel}`, 52, memTopY + 3);
  ctx.restore();
};

const strokeLine = (
  ctx: CanvasRenderingContext2D,
  points: ReadonlyArray<{ i: number; pct: number; y: number }>,
  n: number,
  w: number,
  ramp: ReadonlyArray<Rgb>,
  xOffset: number,
): void => {
  if (points.length < 2) return;
  ctx.save();
  ctx.lineWidth = 1.5;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    if (curr.i !== prev.i + 1) continue;
    ctx.beginPath();
    ctx.moveTo(pointX(prev.i, n, w) + xOffset, prev.y);
    ctx.lineTo(pointX(curr.i, n, w) + xOffset, curr.y);
    ctx.strokeStyle = valueColor(curr.pct, ramp);
    ctx.stroke();
  }
  ctx.restore();
};

/**
 * Render one frame of a metrics chart onto the hook's canvas. Shared
 * between RunnerMetricsChart and JobMetricsChart. Mutates `_chart` with
 * the computed ChartPoint[] so future interaction code can read it.
 */
export const redrawMetrics = (
  hook: MetricsRenderHook,
  config: MetricsRenderConfig,
): void => {
  if (!hook._canvas || !hook._ctx) return;
  void config.idBase;

  const chart = computeMetricsChart(hook._samples, hook._cores, hook._ramBytes);
  const renderChart = leadingEdgeChart(
    hook._samples,
    hook._cores,
    hook._ramBytes,
  );
  hook._chart = chart;
  const n = renderChart.length;
  const w = hook._w;
  const h = hook._h;
  const ctx = hook._ctx;

  resizeCanvas(hook._canvas, ctx, w, h);

  const axes = computeAxisScales(chart, { w, h });
  const ramps = hook._ramps ?? config.rampsFallback();
  const step = (w - plot.left - plot.right) / MAX_SAMPLES;
  const scrollOffsetSlots = hook._scrollOffsetSlots ?? 0;
  const xOffset = n > 1 ? step * scrollOffsetSlots : 0;

  const cpuFmt = config.formatCpuAxis ?? ((v) => formatMetricsAxisPct(v));
  const memFmt = config.formatMemAxis ?? ((v) => formatMetricsAxisPct(v));
  const cpuLabel = cpuFmt(axes.cpuMax, hook._cores);
  const memLabel = memFmt(axes.memMax, hook._ramBytes);

  ctx.clearRect(0, 0, w, h);
  drawAxisCanvas(ctx, w, h, cpuLabel, memLabel);
  if (n === 0) {
    hook._lastChart = chart;
    hook._lastCpuMax = axes.cpuMax;
    hook._lastMemMax = axes.memMax;
    return;
  }

  const { cpuScale, memScale } = axes;

  ctx.save();
  ctx.beginPath();
  ctx.rect(
    plot.left,
    0,
    Math.max(0, w - plot.left - plot.right - edgeClipPx),
    h,
  );
  ctx.clip();

  strokeLine(
    ctx,
    renderChart.map((d, i) => ({ i, pct: d.memPct, y: memScale(d.memPct) })),
    n,
    w,
    ramps.mem,
    xOffset,
  );

  strokeLine(
    ctx,
    indexedCpu(renderChart).map((p) => ({
      i: p.i,
      pct: p.cpuPct,
      y: cpuScale(p.cpuPct),
    })),
    n,
    w,
    ramps.cpu,
    xOffset,
  );
  ctx.restore();

  hook._lastChart = chart;
  hook._lastCpuMax = axes.cpuMax;
  hook._lastMemMax = axes.memMax;
};
